/**
 * MODULE: domain/pages/frontend/utils/pageActionButtonUtils.ts
 * PURPOSE: Validation and href helpers for action button blocks
 */

export type ActionButtonLinkType = 'external' | 'page';
export type ActionButtonVariant = 'primary' | 'secondary' | 'outline';

export type ActionButtonBlockContent = {
  label: string;
  link_type: ActionButtonLinkType;
  url: string;
  page_id: string | null;
  workspace_id: string | null;
  page_title: string | null;
  variant: ActionButtonVariant;
  open_in_new_tab: boolean;
};

const VARIANTS: ActionButtonVariant[] = ['primary', 'secondary', 'outline'];

export function isActionButtonVariant(value: unknown): value is ActionButtonVariant {
  return typeof value === 'string' && VARIANTS.includes(value as ActionButtonVariant);
}

export function normalizeExternalUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function buildActionButtonHref(content: Pick<ActionButtonBlockContent, 'link_type' | 'url' | 'page_id' | 'workspace_id'>): string | null {
  if (content.link_type === 'page') {
    if (!content.page_id || !content.workspace_id) return null;
    return `/workspaces/${content.workspace_id}/pages/${content.page_id}`;
  }
  return normalizeExternalUrl(content.url);
}

export function buildActionButtonContent(input: {
  label: string;
  linkType: ActionButtonLinkType;
  url?: string;
  pageId?: string | null;
  workspaceId?: string | null;
  pageTitle?: string | null;
  variant?: ActionButtonVariant;
  openInNewTab?: boolean;
}): ActionButtonBlockContent | null {
  const label = input.label.trim();
  if (!label) return null;

  const variant = isActionButtonVariant(input.variant) ? input.variant : 'primary';

  if (input.linkType === 'page') {
    if (!input.pageId || !input.workspaceId) return null;
    return {
      label,
      link_type: 'page',
      url: '',
      page_id: input.pageId,
      workspace_id: input.workspaceId,
      page_title: input.pageTitle?.trim() || 'Untitled',
      variant,
      open_in_new_tab: Boolean(input.openInNewTab),
    };
  }

  const url = normalizeExternalUrl(input.url ?? '');
  if (!url) return null;

  return {
    label,
    link_type: 'external',
    url,
    page_id: null,
    workspace_id: null,
    page_title: null,
    variant,
    open_in_new_tab: input.openInNewTab !== false,
  };
}

export function actionButtonVariantClass(variant: ActionButtonVariant): string {
  switch (variant) {
    case 'secondary':
      return 'bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600';
    case 'outline':
      return 'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800';
    default:
      return 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500';
  }
}

export function readActionButtonFromAttrs(attrs: Record<string, unknown>): ActionButtonBlockContent | null {
  const label = typeof attrs.label === 'string' ? attrs.label.trim() : '';
  if (!label) return null;

  const linkType: ActionButtonLinkType = attrs.linkType === 'page' ? 'page' : 'external';
  const variant = isActionButtonVariant(attrs.variant) ? attrs.variant : 'primary';

  if (linkType === 'page') {
    const pageId = typeof attrs.pageId === 'string' ? attrs.pageId : null;
    const workspaceId = typeof attrs.workspaceId === 'string' ? attrs.workspaceId : null;
    if (!pageId || !workspaceId) return null;
    return {
      label,
      link_type: 'page',
      url: '',
      page_id: pageId,
      workspace_id: workspaceId,
      page_title: typeof attrs.pageTitle === 'string' ? attrs.pageTitle : 'Untitled',
      variant,
      open_in_new_tab: Boolean(attrs.openInNewTab),
    };
  }

  const url = normalizeExternalUrl(typeof attrs.url === 'string' ? attrs.url : '');
  if (!url) return null;

  return {
    label,
    link_type: 'external',
    url,
    page_id: null,
    workspace_id: null,
    page_title: null,
    variant,
    open_in_new_tab: attrs.openInNewTab !== false,
  };
}

export function actionButtonContentToAttrs(
  content: ActionButtonBlockContent,
  host?: { workspaceId: string; pageId: string }
): Record<string, unknown> {
  return {
    label: content.label,
    linkType: content.link_type,
    url: content.url,
    pageId: content.page_id,
    workspaceId: content.workspace_id ?? host?.workspaceId ?? null,
    pageTitle: content.page_title,
    variant: content.variant,
    openInNewTab: content.open_in_new_tab,
    hostPageId: host?.pageId ?? null,
  };
}
